import { useState, useCallback } from 'react';
import API from "../../../../API";
import { emptyWalletForm } from './constants';

export const useWalletManagement = (showSnackbar) => {
  const [wallets, setWallets] = useState([]);
  const [primaryWalletId, setPrimaryWalletId] = useState(null);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [walletDialog, setWalletDialog] = useState({ open: false, mode: "create", wallet: null });
  const [newWallet, setNewWallet] = useState(emptyWalletForm());

  const fetchWallets = useCallback(async () => {
    try {
      setWalletsLoading(true);
      const response = await API.creatorConnect.getWallets();
      setWallets(response.wallets || []);
      setPrimaryWalletId(response.primary_wallet_id ?? null);
    } catch (error) {
      console.error("Failed to fetch wallets:", error);
      showSnackbar("Failed to load payment methods", "error");
    } finally {
      setWalletsLoading(false);
    }
  }, [showSnackbar]);

  const handleWalletChange = useCallback((field, value) => {
    setNewWallet(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCreateWallet = useCallback(async () => {
    try {
      await API.creatorConnect.createWallet(newWallet);
      showSnackbar("Payment method added successfully!");
      setWalletDialog({ open: false, mode: "create", wallet: null });
      setNewWallet(emptyWalletForm());
      await fetchWallets();
    } catch (error) {
      showSnackbar(error?.response?.data?.error || "Failed to add payment method", "error");
    }
  }, [newWallet, fetchWallets, showSnackbar]);

  const handleUpdateWallet = useCallback(async () => {
    try {
      if (!walletDialog.wallet?.id) return;
      await API.creatorConnect.updateWallet(walletDialog.wallet.id, newWallet);
      showSnackbar("Payment method updated successfully!");
      setWalletDialog({ open: false, mode: "create", wallet: null });
      setNewWallet(emptyWalletForm());
      await fetchWallets();
    } catch (error) {
      showSnackbar(error?.response?.data?.error || "Failed to update payment method", "error");
    }
  }, [newWallet, walletDialog, fetchWallets, showSnackbar]);

  const handleDeleteWallet = useCallback(async (walletId) => {
    try {
      await API.creatorConnect.deleteWallet(walletId);
      await fetchWallets();
      showSnackbar("Payment method removed successfully!");
    } catch (error) {
      showSnackbar(error?.response?.data?.error || "Failed to remove payment method", "error");
    }
  }, [fetchWallets, showSnackbar]);

  const handleSetPrimaryWallet = useCallback(async (walletId) => {
    try {
      await API.creatorConnect.setPrimaryWallet(walletId);
      await fetchWallets();
      showSnackbar("Primary payment method updated successfully!");
    } catch (error) {
      showSnackbar(error?.response?.data?.error || "Failed to update primary payment method", "error");
    }
  }, [fetchWallets, showSnackbar]);

  const handleOpenWalletDialog = useCallback((wallet = null) => {
    if (wallet) {
      setNewWallet({
        wallet_type: wallet.wallet_type || "",
        nickname: wallet.nickname || "",
        paypal_email: wallet.paypal_email || "",
        wise_email: wallet.wise_email || "",
        wise_currency: wallet.wise_currency || "USD",
        wise_profile_id: wallet.wise_profile_id || "",
        wise_recipient_id: wallet.wise_recipient_id || "",
        bank_account_type: wallet.bank_account_type || "checking",
        bank_routing_number: wallet.bank_routing_number || "",
        bank_account_number: wallet.bank_account_number || "",
        bank_account_holder_name: wallet.bank_account_holder_name || "",
        bank_name: wallet.bank_name || "",
        bank_address: wallet.bank_address || "",
        swift_code: wallet.swift_code || "",
        iban: wallet.iban || "",
        crypto_currency: wallet.crypto_currency || "BTC",
        crypto_wallet_address: wallet.crypto_wallet_address || "",
        crypto_network: wallet.crypto_network || "mainnet",
      });
      setWalletDialog({ open: true, mode: "edit", wallet });
    } else {
      setNewWallet(emptyWalletForm());
      setWalletDialog({ open: true, mode: "create", wallet: null });
    }
  }, []);

  const handleCloseWalletDialog = useCallback(() => {
    setWalletDialog({ open: false, mode: "create", wallet: null });
    setNewWallet(emptyWalletForm());
  }, []);

  const handleSubmitWallet = useCallback(() => {
    if (walletDialog.mode === "edit") {
      handleUpdateWallet();
    } else {
      handleCreateWallet();
    }
  }, [walletDialog.mode, handleUpdateWallet, handleCreateWallet]);

  return {
    wallets,
    walletsLoading,
    walletDialog,
    newWallet,
    fetchWallets,
    handleWalletChange,
    handleDeleteWallet,
    handleSetPrimaryWallet,
    handleOpenWalletDialog,
    handleCloseWalletDialog,
    handleSubmitWallet,
  };
};